import React, { useState, useEffect } from 'react';
import { useRooms } from '../../context/RoomContext';
import { format, addDays, differenceInDays, parseISO, isAfter, isSameDay, startOfDay } from 'date-fns';
import { 
  Calendar, 
  Phone, 
  Mail, 
  User, 
  AlertCircle, 
  Users, 
  Check, 
  Clock, 
  X, 
  Plus,
  Edit,
  Eye,
  Filter,
  Search,
  Hotel,
  DollarSign,
  Percent,
  Tag,
  Trash2,
   ChevronLeft, ChevronRight
} from 'lucide-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

interface BookingFormData {
  roomId: string;
  userName: string;
  userEmail: string;
  userPhone: string;
  checkIn: string;
  checkOut: string;
  source: string;
  isExternal: boolean;
  discountType: 'none' | 'percentage' | 'fixed';
  discountValue: number;
  adminNotes: string;
  userNotes: string;
  selectedRoom: string
}

const AdminBookings: React.FC = () => {
  const { 
    rooms, 
    bookings, 
    bookingSources, 
    bookRoom, 
    addExternalBooking, 
    updateBookingStatus,
    deleteBooking,
    addBookingSource,
    deleteBookingSource,
    hasDateConflict,
    isRoomBookable ,
    getBookedDatesForRoom
  } = useRooms();

  // Form state
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [formData, setFormData] = useState<BookingFormData>({
    roomId: '',
    userName: '',
    userEmail: '',
    userPhone: '',
    checkIn: format(new Date(), 'yyyy-MM-dd'),
    checkOut: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    source: 'Manual',
    isExternal: false,
    discountType: 'none',
    discountValue: 0,
    adminNotes: '',
    userNotes: '',
    selectedRoom: '',
  });
  const navigate = useNavigate();

  // UI state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSourceForm, setShowSourceForm] = useState(false);
  const [newSource, setNewSource] = useState('');
  const [showDiscount, setShowDiscount] = useState(false);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('all');
  const [roomFilter, setRoomFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState(new Date().getMonth());
  const [currentCalendarYear, setCurrentCalendarYear] = useState(new Date().getFullYear());
  // Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState<string | null>(null);
  // Set default room when rooms are loaded
  useEffect(() => {
    if (rooms.length > 0 && !formData.roomId) {
      setFormData(prev => ({ ...prev, roomId: rooms[0].id }));
    }
  }, [rooms, formData.roomId]);

  // Calculate pricing with discount
  const calculatePricing = () => {
    if (!formData.roomId || !formData.checkIn || !formData.checkOut) {
      return { originalPrice: 0, discountAmount: 0, finalPrice: 0, nights: 0 };
    }

    const room = rooms.find(r => r.id === formData.roomId);
    if (!room) return { originalPrice: 0, discountAmount: 0, finalPrice: 0, nights: 0 };

    const checkInDate = parseISO(formData.checkIn);
    const checkOutDate = parseISO(formData.checkOut);
    const nights = isAfter(checkOutDate, checkInDate) ? differenceInDays(checkOutDate, checkInDate) : 0;
    const originalPrice = room.price * nights;

    let discountAmount = 0;
    if (formData.discountType === 'percentage') {
      discountAmount = (originalPrice * formData.discountValue) / 100;
    } else if (formData.discountType === 'fixed') {
      discountAmount = Math.min(formData.discountValue, originalPrice);
    }

    const finalPrice = Math.max(0, originalPrice - discountAmount);

    return { originalPrice, discountAmount, finalPrice, nights };
  };

  const { originalPrice, discountAmount, finalPrice, nights } = calculatePricing();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'discountValue') {
      setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    setError('');
  };
  const [selectedRoom, setSelectedRoom] = useState<string>('all');
  const [showCalendar, setShowCalendar] = useState<'checkIn' | 'checkOut' | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Validation
      if (!formData.roomId) {
        throw new Error('Please select a room');
      }

      if (!formData.userName.trim()) {
        throw new Error('Guest name is required');
      }

      if (!formData.userEmail.trim()) {
        throw new Error('Guest email is required');
      }

      const room = rooms.find(r => r.id === formData.roomId);
      if (!room) {
        throw new Error('Invalid room selection');
      }

      if (!isRoomBookable(formData.roomId)) {
        throw new Error('This room is currently not available for booking');
      }

      const checkInDate = parseISO(formData.checkIn);
      const checkOutDate = parseISO(formData.checkOut);
      
      if (!isAfter(checkOutDate, checkInDate)) {
        throw new Error('Check-out date must be after check-in date');
      }

      if (hasDateConflict(formData.roomId, formData.checkIn, formData.checkOut)) {
        throw new Error('Selected dates are not available for this room');
      }

      // Create booking data
      const bookingData = {
        roomId: formData.roomId,
        userId: null,
        userName: formData.userName,
        userEmail: formData.userEmail,
        userPhone: formData.userPhone,
        checkIn: formData.checkIn,
        checkOut: formData.checkOut,
        totalPrice: finalPrice,
        originalPrice: originalPrice,
        discountType: formData.discountType,
        discountValue: formData.discountValue,
        discountAmount: discountAmount,
        status: 'confirmed' as const,
        source: formData.source,
        adminNotes: formData.adminNotes,
        userNotes: formData.userNotes
      };

      if (formData.isExternal) {
        await addExternalBooking(bookingData);
      } else {
        await bookRoom(bookingData);
      }

      setSuccess('Booking created successfully!');
      
      // Reset form
      setFormData({
        roomId: rooms[0]?.id || '',
        userName: '',
        userEmail: '',
        userPhone: '',
        checkIn: format(new Date(), 'yyyy-MM-dd'),
        checkOut: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
        source: 'Manual',
        isExternal: false,
        discountType: 'none',
        discountValue: 0,
        adminNotes: '',
        userNotes: '',
        selectedRoom: '',
      });
      setShowDiscount(false);
      setShowBookingForm(false);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Booking error:', error);
      setError(error instanceof Error ? error.message : 'Failed to create booking');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddSource = async () => {
    if (!newSource.trim()) return;
    
    try {
      await addBookingSource(newSource.trim());
      setNewSource('');
      setShowSourceForm(false);
    } catch (error) {
      console.error('Error adding source:', error);
    }
  };

  const handleDeleteSource = async (source: string) => {
    if (source === 'Manual') return; // Don't allow deleting Manual
    
    try {
      await deleteBookingSource(source);
    } catch (error) {
      console.error('Error deleting source:', error);
    }
  };

  // Filter bookings
  const filteredBookings = bookings.filter(booking => {
    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
    const matchesRoom = roomFilter === 'all' || booking.roomId === roomFilter;
    const matchesSearch = !searchTerm || 
      booking.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (booking.userPhone && booking.userPhone.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesStatus && matchesRoom && matchesSearch;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <Check className="h-4 w-4" />;
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'cancelled':
        return <X className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };
  const handleBookingClick = (bookingId: string) => {
    navigate(`/admin/bookings/${bookingId}`);
  };
    const getEventColor = (status: string, opacity: number = 1) => {
    const colors = {
      'confirmed': `rgba(5, 150, 105, ${opacity})`, // green-600
      'pending': `rgba(217, 119, 6, ${opacity})`, // yellow-600
      'cancelled': `rgba(220, 38, 38, ${opacity})`, // red-600
      'default': `rgba(107, 114, 128, ${opacity})` // gray-500
    };
    return colors[status as keyof typeof colors] || colors.default;
  };
    // Enhanced calendar events to properly handle 12 PM to 12 PM system and overlaps
  const calendarEvents = React.useMemo(() => {
    const filteredBookings = bookings.filter(booking => 
      selectedRoom === 'all' || booking.roomId === selectedRoom
    );

    // Group bookings by room to handle overlaps
    const bookingsByRoom = filteredBookings.reduce((acc, booking) => {
      if (!acc[booking.roomId]) {
        acc[booking.roomId] = [];
      }
      acc[booking.roomId].push(booking);
      return acc;
    }, {} as Record<string, typeof bookings>);

    const events: any[] = [];

    Object.entries(bookingsByRoom).forEach(([roomId, roomBookings]) => {
      const room = rooms.find(r => r.id === roomId);
      
      // Sort bookings by check-in date
      const sortedBookings = [...roomBookings].sort((a, b) => 
        new Date(a.checkIn).getTime() - new Date(b.checkIn).getTime()
      );

      sortedBookings.forEach((booking, index) => {
        const checkInDate = parseISO(booking.checkIn);
        const checkOutDate = parseISO(booking.checkOut);
        const nights = differenceInDays(checkOutDate, checkInDate);
        
        // Calculate overlap level for visual stacking
        let overlapLevel = 0;
        for (let i = 0; i < index; i++) {
          const otherBooking = sortedBookings[i];
          const otherCheckIn = parseISO(otherBooking.checkIn);
          const otherCheckOut = parseISO(otherBooking.checkOut);
          
          // Check if there's any overlap
          if (checkInDate < otherCheckOut && checkOutDate > otherCheckIn) {
            overlapLevel++;
          }
        }

        // Create the event with proper styling for overlaps
        const event = {
          id: `${booking.id}-${index}`,
          title: `${booking.userName} - ${room?.name}`,
          start: booking.checkIn,
          end: booking.checkOut,
          backgroundColor: getEventColor(booking.status, 0.8),
          borderColor: getEventColor(booking.status, 1),
          textColor: '#ffffff',
          allDay: true,
          display: 'block',
          extendedProps: {
            booking: booking,
            room: room,
            nights: nights,
            checkInTime: '12:00 PM',
            checkOutTime: '12:00 PM',
            overlapLevel: overlapLevel,
            roomId: roomId
          },
          // Custom rendering properties for overlaps
          classNames: [`overlap-level-${overlapLevel}`],
          // Adjust event positioning for overlaps
          constraint: roomId,
          overlap: true,
          rendering: 'background'
        };

        events.push(event);

        // Add a foreground event for better visibility
        events.push({
          ...event,
          id: `${booking.id}-fg-${index}`,
          rendering: undefined,
          display: 'block',
          backgroundColor: 'transparent',
          borderColor: getEventColor(booking.status, 1),
          borderWidth: 2,
          textColor: getEventColor(booking.status, 1),
          classNames: [`overlap-level-${overlapLevel}`, 'booking-foreground']
        });
      });
    });

    return events;
  }, [bookings, selectedRoom, rooms]);
  const bookedDates = formData.roomId ? getBookedDatesForRoom(formData.roomId) : [];
  console.log('booked tdates',bookedDates,'selected room ',formData.roomId)
  // Check if a specific date is unavailable
  const isDateUnavailable = (date: string) => {
    return bookedDates.includes(date);
  };
    const handleDateSelect = (date: string, type: 'checkIn' | 'checkOut') => {
    const newData = { ...formData, [type]: date };
    
    if (type === 'checkIn') {
      // Auto-adjust checkout if it's not after the new check-in
      const checkInDate = parseISO(date);
      const checkOutDate = parseISO(newData.checkOut);
      
      if (!isAfter(checkOutDate, checkInDate)) {
        newData.checkOut = format(addDays(checkInDate, 1), 'yyyy-MM-dd');
      }
    }

    setFormData(newData);
    setShowCalendar(null);

    // Validate the new dates
    if (newData.selectedRoom && newData.checkIn && newData.checkOut) {
      const checkInDate = parseISO(newData.checkIn);
      const checkOutDate = parseISO(newData.checkOut);
      
      // Check if room is bookable
      if (!isRoomBookable(newData.selectedRoom)) {
        setError('This room is currently not available for booking');
        return;
      }
      
      if (hasDateConflict(newData.selectedRoom, newData.checkIn, newData.checkOut)) {
        setError('Selected dates are not available for this room. Please choose different dates.');
      } else {
        setError('');
      }
    }
  };
    // Navigate to previous month
  const goToPreviousMonth = () => {
    if (currentCalendarMonth === 0) {
      setCurrentCalendarMonth(11);
      setCurrentCalendarYear(currentCalendarYear - 1);
    } else {
      setCurrentCalendarMonth(currentCalendarMonth - 1);
    }
  };

  // Navigate to next month
  const goToNextMonth = () => {
    if (currentCalendarMonth === 11) {
      setCurrentCalendarMonth(0);
      setCurrentCalendarYear(currentCalendarYear + 1);
    } else {
      setCurrentCalendarMonth(currentCalendarMonth + 1);
    }
  };

  // Generate calendar days for display
// Generate calendar days for a specific month
  const generateCalendarDays = (month: number, year: number) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const renderCalendar = (type: 'checkIn' | 'checkOut') => {
    const currentDate = new Date();
    const calendarDays = generateCalendarDays(currentCalendarMonth, currentCalendarYear);
    const today = format(currentDate, 'yyyy-MM-dd');
    const displayDate = new Date(currentCalendarYear, currentCalendarMonth, 1);
    
    return (
      <div className="absolute z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 w-80">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">
            Select {type === 'checkIn' ? 'Check-in' : 'Check-out'} Date
          </h3>
          <button
            onClick={() => setShowCalendar(null)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={goToPreviousMonth}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h4 className="font-medium">{format(displayDate, 'MMMM yyyy')}</h4>
          <button
            onClick={goToNextMonth}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((day, index) => {
            if (!day) {
              return <div key={index} className="h-8"></div>;
            }
            
            const dateStr = format(day, 'yyyy-MM-dd');
            const isToday = dateStr === today;
            const isPast = day < startOfDay(currentDate);
            const isUnavailable = isDateUnavailable(dateStr);
            const isSelected = dateStr === formData[type];
            
            // For check-out, disable dates that are not after check-in
            const isInvalidCheckOut = type === 'checkOut' && !isAfter(day, parseISO(formData.checkIn));
            
            const isDisabled = isPast || isUnavailable || isInvalidCheckOut;
            
            return (
              <button
                key={index}
                onClick={() => !isDisabled && handleDateSelect(dateStr, type)}
                disabled={isDisabled}
                className={`
                  h-8 w-8 text-sm rounded-md transition-colors
                  ${isSelected 
                    ? 'bg-indigo-600 text-white' 
                    : isToday 
                    ? 'bg-indigo-100 text-indigo-600 font-medium'
                    : isDisabled
                    ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                    : 'hover:bg-indigo-50 text-gray-700'
                  }
                  ${isUnavailable && !isSelected ? 'bg-red-50 text-red-400 line-through' : ''}
                `}
              >
                {day.getDate()}
              </button>
            );
          })}
        </div>
        
        <div className="mt-4 text-xs text-gray-500">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-50 border border-red-200 rounded mr-1"></div>
              <span>Unavailable</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-indigo-100 rounded mr-1"></div>
              <span>Today</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-indigo-600 rounded mr-1"></div>
              <span>Selected</span>
            </div>
          </div>
        </div>
      </div>
    );
  };
  return (
    <div className="px-4 py-6 sm:px-0">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Manage Bookings</h2>
        <button
          onClick={() => setShowBookingForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Booking
        </button>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-4 bg-green-50 p-4 rounded-md">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-select"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Room</label>
            <select
              value={roomFilter}
              onChange={(e) => {
                setSelectedRoom(e.target.value)
                setRoomFilter(e.target.value)
              }}
              className="form-select"
            >
              <option value="all">All Rooms</option>
              {rooms.map(room => (
                <option key={room.id} value={room.id}>{room.name}</option>
              ))}
            </select>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input-with-icon"
                placeholder="Search by name, email, or phone..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Calendar View */}
      <div className="bg-white shadow rounded-lg mb-6 p-4">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Booking Calendar</h3>
          <div className="text-sm text-gray-600 mb-4">
            <div className="flex flex-wrap items-center gap-6 mb-2">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-yellow-600 rounded mr-2"></div>
                <span>Pending</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-green-600 rounded mr-2"></div>
                <span>Confirmed</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-red-600 rounded mr-2"></div>
                <span>Cancelled</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gray-400 border-2 border-gray-600 rounded mr-2"></div>
                <span>Overlapping Bookings</span>
              </div>
            </div>
            <div className="flex items-center text-xs text-gray-500">
              <Clock className="h-3 w-3 mr-1" />
              <span>All bookings operate on 12:00 PM check-in/check-out schedule. Overlapping bookings are stacked vertically.</span>
            </div>
          </div>
        </div>
        <FullCalendar
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          events={calendarEvents}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth'
          }}
          height="auto"
          eventDisplay="block"
          dayMaxEvents={false}
          moreLinkClick="popover"
          eventOverlap={true}
          slotEventOverlap={true}
          eventContent={(eventInfo) => {
            const booking = eventInfo.event.extendedProps.booking;
            const room = eventInfo.event.extendedProps.room;
            const nights = eventInfo.event.extendedProps.nights;
            const overlapLevel = eventInfo.event.extendedProps.overlapLevel;
            
            return (
              <div 
                className={`p-1 cursor-pointer text-white text-xs overflow-hidden ${
                  eventInfo.event.classNames.includes('booking-foreground') ? 'font-semibold' : ''
                }`}
                onClick={() => handleBookingClick(booking.id)}
                title={`${booking.userName} - ${room?.name} (${nights} nights)\nCheck-in: ${format(parseISO(booking.checkIn), 'MMM d')} 12:00 PM\nCheck-out: ${format(parseISO(booking.checkOut), 'MMM d')} 12:00 PM\n${overlapLevel > 0 ? `Overlap Level: ${overlapLevel + 1}` : ''}`}
                style={{
                  marginTop: overlapLevel > 0 ? `${overlapLevel * 2}px` : '0px',
                  position: 'relative',
                  zIndex: 10 + overlapLevel
                }}
              >
                <div className="font-semibold truncate">{booking.userName}</div>
                <div className="truncate">{room?.name}</div>
                <div className="italic">{nights} night{nights !== 1 ? 's' : ''}</div>
                {booking.source && booking.source !== 'Manual' && (
                  <div className="text-xs opacity-75">{booking.source}</div>
                )}
                {overlapLevel > 0 && (
                  <div className="text-xs opacity-75">#{overlapLevel + 1}</div>
                )}
              </div>
            );
          }}
          eventClick={(info) => {
            const booking = info.event.extendedProps.booking;
            handleBookingClick(booking.id);
          }}
          eventDidMount={(info) => {
            // Add detailed tooltip with booking information
            const booking = info.event.extendedProps.booking;
            const room = info.event.extendedProps.room;
            const nights = info.event.extendedProps.nights;
            const overlapLevel = info.event.extendedProps.overlapLevel;
            
            const checkInFormatted = format(parseISO(booking.checkIn), 'MMM d, yyyy');
            const checkOutFormatted = format(parseISO(booking.checkOut), 'MMM d, yyyy');
            
            info.el.title = [
              `Guest: ${booking.userName}`,
              `Room: ${room?.name}`,
              `Check-in: ${checkInFormatted} at 12:00 PM`,
              `Check-out: ${checkOutFormatted} at 12:00 PM`,
              `Duration: ${nights} night${nights !== 1 ? 's' : ''}`,
              `Status: ${booking.status}`,
              `Source: ${booking.source || 'Manual'}`,
              `Total: $${booking.totalPrice}`,
              overlapLevel > 0 ? `Overlap Level: ${overlapLevel + 1}` : ''
            ].filter(Boolean).join('\n');

            // Apply custom styling for overlaps
            if (overlapLevel > 0) {
              info.el.style.marginTop = `${overlapLevel * 2}px`;
              info.el.style.position = 'relative';
              info.el.style.zIndex = `${10 + overlapLevel}`;
            }
          }}
        />
      </div>
      

      {/* Bookings List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {filteredBookings.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No bookings found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {bookings.length === 0 
                ? "Get started by creating your first booking."
                : "Try adjusting your filters to see more results."
              }
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {filteredBookings.map((booking) => {
              const room = rooms.find(r => r.id === booking.roomId);
              const payments = booking.payments || [];
              const totalPaid = payments.reduce((sum, payment) => sum + (payment?.amount || 0), 0);
              const remainingAmount = booking.totalPrice - totalPaid;
              
              return (
                <li key={booking.id} className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-12 w-12">
                        <img
                          className="h-12 w-12 rounded-lg object-cover"
                          src={room?.image_url || '/placeholder-room.jpg'}
                          alt={room?.name || 'Room'}
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-indigo-600">
                          IFA-{booking.id.substring(0, 8)} - {booking.userName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {room?.name || 'Unknown Room'} • {booking.checkIn} to {booking.checkOut}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {booking.userEmail} {booking.userPhone && `• ${booking.userPhone}`}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      {booking.source && booking.source !== 'Manual' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {booking.source}
                        </span>
                      )}
                      

                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">
                          ${booking.totalPrice}
                        </div>
                        <div className="text-xs text-gray-500">
                          Paid: ${totalPaid} | Due: ${remainingAmount}
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                        {getStatusIcon(booking.status)}
                        <span className="ml-1 capitalize">{booking.status}</span>
                      </span>
                      <Link
                        to={`/admin/bookings/${booking.id}`}
                        className="inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700                                 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <Eye className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">Delete Booking</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-700">
                  Are you sure you want to permanently delete this booking? This will also delete all associated payments and cannot be undone.
                </p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeleteConfirmation(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Delete Booking
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* New Booking Modal */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">New Booking</h3>
                <button
                  onClick={() => {
                    setShowBookingForm(false);
                    setError('');
                    setShowDiscount(false);
                  }}
                  className="text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-4 flex items-center text-sm text-gray-600">
                <Clock className="h-4 w-4 mr-1" />
                <span>Check-in: 12:00 PM | Check-out: 12:00 PM</span>
              </div>

              {error && (
                <div className="mb-4 bg-red-50 p-4 rounded-md flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Booking Source */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Booking Source
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowSourceForm(!showSourceForm)}
                      className="text-sm text-indigo-600 hover:text-indigo-500"
                    >
                      Edit Sources
                    </button>
                  </div>
                  <select
                    name="source"
                    value={formData.source}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    {bookingSources.map(source => (
                      <option key={source} value={source}>{source}</option>
                    ))}
                  </select>
                </div>

                {/* Source Management */}
                {showSourceForm && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <input
                        type="text"
                        value={newSource}
                        onChange={(e) => setNewSource(e.target.value)}
                        placeholder="Add new source..."
                        className="form-input flex-1"
                      />
                      <button
                        type="button"
                        onClick={handleAddSource}
                        className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                      >
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {bookingSources.map(source => (
                        <span
                          key={source}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-gray-100 text-gray-800"
                        >
                          {source}
                          {source !== 'Manual' && (
                            <button
                              type="button"
                              onClick={() => handleDeleteSource(source)}
                              className="ml-1.5 text-gray-400 hover:text-gray-600"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* External Booking Checkbox */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isExternal"
                    checked={formData.isExternal}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    This is an external booking
                  </label>
                </div>

                {/* Room Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Room
                  </label>
                  <select
                    name="roomId"
                    value={formData.roomId}
                    onChange={handleInputChange}
                    className="form-select"
                    required
                  >
                    <option value="">Select a room...</option>
                    {rooms.filter(room => room.available).map(room => (
                      <option key={room.id} value={room.id}>
                        {room.name} - ${room.price}/night
                      </option>
                    ))}
                  </select>
                </div>

                {/* Dates */}
               <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="relative">
                    <label htmlFor="checkIn" className="block text-sm font-medium text-gray-700">
                      Check-in Date (12:00 PM)
                    </label>
                    <div className="mt-1 relative">
                      <input
                        type="date"
                        name="checkIn"
                        id="checkIn"
                        required
                        //min={format(new Date(), 'yyyy-MM-dd')}
                        className={`form-input ${
                          isDateUnavailable(formData.checkIn) ? 'border-red-300 bg-red-50' : ''
                        }`}
                        value={formData.checkIn}
                        onChange={handleInputChange}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCalendar(showCalendar === 'checkIn' ? null : 'checkIn')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <Calendar className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>
                    {isDateUnavailable(formData.checkIn) && (
                      <p className="mt-1 text-xs text-red-600">This date is not available</p>
                    )}
                    {showCalendar === 'checkIn' && renderCalendar('checkIn')}
                  </div>

                  <div className="relative">
                    <label htmlFor="checkOut" className="block text-sm font-medium text-gray-700">
                      Check-out Date (12:00 PM)
                    </label>
                    <div className="mt-1 relative">
                      <input
                        type="date"
                        name="checkOut"
                        id="checkOut"
                        required
                        //min={getMinCheckOutDate()}
                        className={`form-input ${
                          isDateUnavailable(formData.checkOut) ? 'border-red-300 bg-red-50' : ''
                        }`}
                        value={formData.checkOut}
                        onChange={handleInputChange}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCalendar(showCalendar === 'checkOut' ? null : 'checkOut')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <Calendar className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>
                    {isDateUnavailable(formData.checkOut) && (
                      <p className="mt-1 text-xs text-red-600">This date is not available</p>
                    )}
                    {showCalendar === 'checkOut' && renderCalendar('checkOut')}
                  </div>
                </div>

                {/* Guest Information */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Guest Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="userName"
                      value={formData.userName}
                      onChange={handleInputChange}
                      className="form-input-with-icon"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Guest Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      name="userEmail"
                      value={formData.userEmail}
                      onChange={handleInputChange}
                      className="form-input-with-icon"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Guest Phone
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      name="userPhone"
                      value={formData.userPhone}
                      onChange={handleInputChange}
                      className="form-input-with-icon"
                    />
                  </div>
                </div>

                {/* Discount Section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Discount
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setShowDiscount(!showDiscount);
                        if (showDiscount) {
                          setFormData(prev => ({
                            ...prev,
                            discountType: 'none',
                            discountValue: 0
                          }));
                        }
                      }}
                      className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${
                        showDiscount
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {showDiscount ? (
                        <>
                          <X className="h-4 w-4 mr-1" />
                          Remove Discount
                        </>
                      ) : (
                        <>
                          <Tag className="h-4 w-4 mr-1" />
                          Add Discount
                        </>
                      )}
                    </button>
                  </div>

                  {showDiscount && (
                    <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Discount Type
                          </label>
                          <select
                            name="discountType"
                            value={formData.discountType}
                            onChange={handleInputChange}
                            className="form-select"
                          >
                            <option value="none">No Discount</option>
                            <option value="percentage">Percentage (%)</option>
                            <option value="fixed">Fixed Amount ($)</option>
                          </select>
                        </div>
                        
                        {formData.discountType !== 'none' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Discount Value
                            </label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                {formData.discountType === 'percentage' ? (
                                  <Percent className="h-5 w-5 text-gray-400" />
                                ) : (
                                  <DollarSign className="h-5 w-5 text-gray-400" />
                                )}
                              </div>
                              <input
                                type="number"
                                name="discountValue"
                                value={formData.discountValue}
                                onChange={handleInputChange}
                                className="form-input-with-icon"
                                placeholder={formData.discountType === 'percentage' ? '10' : '50.00'}
                                min="0"
                                max={formData.discountType === 'percentage' ? '100' : originalPrice.toString()}
                                step={formData.discountType === 'percentage' ? '1' : '0.01'}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Discount Preview */}
                      {formData.discountType !== 'none' && formData.discountValue > 0 && (
                        <div className="bg-blue-50 p-3 rounded-md">
                          <h5 className="text-sm font-medium text-blue-900 mb-2">Discount Preview</h5>
                          <div className="text-sm text-blue-700 space-y-1">
                            <div className="flex justify-between">
                              <span>Original Price:</span>
                              <span>${originalPrice.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Discount:</span>
                              <span className="text-red-600">-${discountAmount.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between font-medium border-t border-blue-200 pt-1">
                              <span>Final Price:</span>
                              <span className="text-green-600">${finalPrice.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Notes Section */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Notes</h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Admin Notes (Internal)
                      </label>
                      <textarea
                        name="adminNotes"
                        value={formData.adminNotes}
                        onChange={handleInputChange}
                        rows={3}
                        className="form-input min-h-[80px]"
                        placeholder="Internal notes for staff use only..."
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        These notes are only visible to admin staff and will not be shared with guests.
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Guest Notes/Special Requests
                      </label>
                      <textarea
                        name="userNotes"
                        value={formData.userNotes}
                        onChange={handleInputChange}
                        rows={3}
                        className="form-input min-h-[80px]"
                        placeholder="Guest special requests, dietary requirements, preferences..."
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Notes and special requests from the guest.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Booking Summary */}
                {formData.roomId && nights > 0 && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Booking Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Room:</span>
                        <span>{rooms.find(r => r.id === formData.roomId)?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Duration:</span>
                        <span>{nights} night{nights !== 1 ? 's' : ''}</span>
                      </div>
                      {formData.discountType !== 'none' && discountAmount > 0 ? (
                        <>
                          <div className="flex justify-between">
                            <span>Original Price:</span>
                            <span>${originalPrice.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-red-600">
                            <span>Discount:</span>
                            <span>-${discountAmount.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between font-medium text-lg border-t pt-2">
                            <span>Final Total:</span>
                            <span className="text-indigo-600">${finalPrice.toFixed(2)}</span>
                          </div>
                        </>
                      ) : (
                        <div className="flex justify-between font-medium text-lg border-t pt-2">
                          <span>Total:</span>
                          <span className="text-indigo-600">${originalPrice.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowBookingForm(false);
                      setError('');
                      setShowDiscount(false);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !formData.roomId}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Creating...' : 'Create Booking'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBookings;